/**
 * 認證相關 API
 */

import { CONFIG } from '@/config';
import { apiClient } from './client';
import type { AuthVerifyResponse, IpCheckResponse } from '@/types/api';

// ========================================================================
// IP 檢查
// ========================================================================

/**
 * 檢查 IP 是否允許存取
 */
export async function checkIpAccess(): Promise<IpCheckResponse> {
  return apiClient.get<IpCheckResponse>('/api/v1/ip-check', {
    requireAuth: false,
    timeout: 5000,
  });
}

// ========================================================================
// 認證驗證
// ========================================================================

/**
 * 驗證 Google 登入
 */
export async function verifyGoogleToken(idToken: string): Promise<AuthVerifyResponse> {
  return apiClient.post<AuthVerifyResponse>('/api/v1/auth/verify', {
    body: {
      id_token: idToken,
      provider: 'google',
      app: 'raiseplan',
    },
    requireAuth: false,
  });
}

/**
 * 驗證 Microsoft 登入
 */
export async function verifyMicrosoftToken(idToken: string): Promise<AuthVerifyResponse> {
  return apiClient.post<AuthVerifyResponse>('/api/v1/auth/verify', {
    body: {
      id_token: idToken,
      provider: 'microsoft',
      app: 'raiseplan',
    },
    requireAuth: false,
  });
}

/**
 * 取得當前使用者資訊
 */
export async function getCurrentUser(): Promise<AuthVerifyResponse['user']> {
  const response = await apiClient.get<{ user: AuthVerifyResponse['user'] }>('/api/v1/auth/me');
  return response.user;
}

/**
 * 登出
 */
export async function logoutFromServer(): Promise<void> {
  try {
    await apiClient.post('/api/v1/auth/logout');
  } catch (error) {
    // 登出失敗不影響本地登出
    console.warn('[Auth] Server logout failed:', error);
  }
}

// ========================================================================
// OAuth 設定
// ========================================================================

/** Google OAuth 設定 */
export const googleOAuthConfig = {
  clientId: CONFIG.GOOGLE_CLIENT_ID,
};

/** Microsoft OAuth 設定 */
export const microsoftOAuthConfig = {
  clientId: CONFIG.MICROSOFT_CLIENT_ID,
  authority: CONFIG.MICROSOFT_AUTHORITY,
  redirectUri: window.location.origin,
  scopes: ['openid', 'profile', 'email'],
};

// ========================================================================
// OAuth 登入封裝（使用 CDN 載入的 SDK）
// ========================================================================

declare const google: {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (response: { credential: string }) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
        use_fedcm_for_prompt?: boolean;
      }) => void;
      prompt: (callback?: (notification: { isNotDisplayed: () => boolean; isSkippedMoment: () => boolean }) => void) => void;
      renderButton: (element: HTMLElement, config: { theme?: string; size?: string; text?: string }) => void;
    };
    oauth2: {
      initTokenClient: (config: {
        client_id: string;
        scope: string;
        callback: (response: { access_token?: string; error?: string }) => void;
      }) => { requestAccessToken: () => void };
    };
  };
};

declare const msal: {
  PublicClientApplication: new (config: {
    auth: { clientId: string; authority: string; redirectUri: string };
  }) => {
    loginPopup: (config: { scopes: string[] }) => Promise<{ idToken: string }>;
  };
};

import type { User } from '@/types';
import { ENV } from '@/config';

/** 登入結果（包含使用者和 Token） */
export interface SignInResult {
  user: User;
  token: string;
}

/**
 * 使用 Google 登入
 * 優先使用 FedCM，失敗時 fallback 到傳統 OAuth 彈窗
 */
export async function signInWithGoogle(): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    let resolved = false;

    const handleCredential = async (credential: string) => {
      if (resolved) return;
      resolved = true;
      try {
        const result = await verifyGoogleToken(credential);
        resolve({
          user: {
            id: result.user.id ?? result.user.email,  // 使用 id 或 email 作為 id
            email: result.user.email,
            name: result.user.name,
            picture: result.user.picture,
            provider: 'google',
          },
          token: result.access_token,  // 使用 access_token
        });
      } catch (error) {
        reject(error);
      }
    };

    try {
      // 🔧 在 localhost 環境下禁用 FedCM，避免 "無法繼續使用 google.com" 錯誤
      // FedCM 需要 HTTPS 和特定 CORS headers，localhost 無法滿足這些要求
      const useFedCm = !ENV.IS_LOCAL;

      google.accounts.id.initialize({
        client_id: googleOAuthConfig.clientId,
        callback: (response) => handleCredential(response.credential),
        auto_select: false,
        cancel_on_tap_outside: false,
        use_fedcm_for_prompt: useFedCm,
      });

      // 嘗試 One Tap，如果失敗則 fallback 到 OAuth 彈窗
      google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // FedCM/One Tap 無法顯示，使用傳統 OAuth 彈窗
          console.log('[Auth] One Tap 不可用，使用 OAuth 彈窗');
          fallbackToOAuthPopup(handleCredential, reject);
        }
      });
    } catch (error) {
      // 初始化失敗，嘗試 OAuth 彈窗
      console.log('[Auth] GSI 初始化失敗，使用 OAuth 彈窗');
      fallbackToOAuthPopup(handleCredential, reject);
    }
  });
}

/**
 * Fallback: 顯示 Google 登入按鈕（當 One Tap 不可用時）
 * 使用 renderButton 方法，這是 Google 官方推薦的方式
 */
function fallbackToOAuthPopup(
  onSuccess: (credential: string) => void,
  onError: (error: Error) => void
): void {
  // 檢查是否已經有 fallback 按鈕容器
  let container = document.getElementById('google-signin-fallback');

  if (!container) {
    // 創建一個覆蓋層來顯示 Google 登入按鈕
    const overlay = document.createElement('div');
    overlay.id = 'google-signin-overlay';
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
    `;

    const modal = document.createElement('div');
    modal.style.cssText = `
      background: white;
      padding: 32px;
      border-radius: 16px;
      text-align: center;
      max-width: 400px;
    `;

    const title = document.createElement('h3');
    title.textContent = '請點擊下方按鈕登入';
    title.style.cssText = 'margin: 0 0 8px 0; font-size: 18px; color: #333;';

    const subtitle = document.createElement('p');
    subtitle.textContent = '由於瀏覽器限制，需要使用標準登入按鈕';
    subtitle.style.cssText = 'margin: 0 0 24px 0; font-size: 14px; color: #666;';

    container = document.createElement('div');
    container.id = 'google-signin-fallback';
    container.style.cssText = 'display: flex; justify-content: center;';

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = '取消';
    cancelBtn.style.cssText = `
      margin-top: 16px;
      padding: 8px 24px;
      border: 1px solid #ddd;
      border-radius: 8px;
      background: white;
      cursor: pointer;
      font-size: 14px;
    `;
    cancelBtn.onclick = () => {
      overlay.remove();
      onError(new Error('登入已取消'));
    };

    modal.appendChild(title);
    modal.appendChild(subtitle);
    modal.appendChild(container);
    modal.appendChild(cancelBtn);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // 重新初始化並渲染按鈕
    // 🔧 在 localhost 環境下禁用 FedCM
    google.accounts.id.initialize({
      client_id: googleOAuthConfig.clientId,
      callback: (response) => {
        overlay.remove();
        onSuccess(response.credential);
      },
      use_fedcm_for_prompt: !ENV.IS_LOCAL,
    });
  }

  // 渲染 Google 登入按鈕
  google.accounts.id.renderButton(container, {
    theme: 'outline',
    size: 'large',
    text: 'signin_with',
  });
}

/**
 * 使用 Microsoft 登入
 */
export async function signInWithMicrosoft(): Promise<SignInResult> {
  try {
    const msalInstance = new msal.PublicClientApplication({
      auth: {
        clientId: microsoftOAuthConfig.clientId,
        authority: microsoftOAuthConfig.authority,
        redirectUri: microsoftOAuthConfig.redirectUri,
      },
    });

    const loginResponse = await msalInstance.loginPopup({
      scopes: microsoftOAuthConfig.scopes,
    });

    const result = await verifyMicrosoftToken(loginResponse.idToken);

    return {
      user: {
        id: result.user.id ?? result.user.email,  // 使用 id 或 email 作為 id
        email: result.user.email,
        name: result.user.name,
        picture: result.user.picture,
        provider: 'microsoft',
      },
      token: result.access_token,  // 使用 access_token
    };
  } catch (error) {
    throw new Error('Microsoft 登入失敗');
  }
}
